import { useState } from "react";
import { Brain, Users, Plus, LogIn } from "lucide-react";
import { useT } from "../lib/i18n";
import { GoogleAd } from "../components/Ads";
import { LegalFooter } from "./LegalFooter";

export default function Home({ onCreate, onJoin, loading, error }) {
  const { t } = useT();
  const [mode, setMode] = useState(null); // 'create' | 'join'
  const [name, setName] = useState("");
  const [code, setCode] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (mode === "create") onCreate(name);
    else onJoin(code.toUpperCase(), name);
  };

  return (
    <div className="grain relative min-h-screen flex flex-col overflow-x-hidden bg-[#050510]">
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#FF007F] opacity-20 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#00FFFF] opacity-20 blur-[120px] pointer-events-none" />

      {/* SEO TEKSTY Z LEWEJ STRONY (Skośny układ brutalist, tylko duże ekrany) */}
      <div className="hidden lg:flex absolute left-8 xl:left-16 top-1/2 -translate-y-1/2 w-72 xl:w-80 flex-col gap-6 z-0 pointer-events-none">
        
        <div className="bg-[#12121A]/80 backdrop-blur-md p-6 rounded-2xl border-2 border-[#00FFFF]/30 -rotate-3 shadow-xl shadow-[#00FFFF]/5">
          <h2 className="font-display font-black text-xl text-[#00FFFF] mb-3 uppercase tracking-wide">
             {t("seo_party_title")}
          </h2>
          <p className="text-sm text-[#A0A0B0] leading-relaxed">
             {t("seo_party_desc")}
          </p>
        </div>

        <div className="bg-[#12121A]/80 backdrop-blur-md p-6 rounded-2xl border-2 border-[#FF007F]/30 rotate-2 shadow-xl shadow-[#FF007F]/5 ml-6">
          <h2 className="font-display font-black text-xl text-[#FF007F] mb-3 uppercase tracking-wide">
             {t("seo_rules_title")}
          </h2>
          <ul className="text-sm text-[#A0A0B0] space-y-2">
            <li><strong className="text-white">{t("seo_rules_1")}</strong></li>
            <li><strong className="text-white">{t("seo_rules_2")}</strong></li>
            <li><strong className="text-white">{t("seo_rules_3")}</strong></li>
          </ul>
        </div>

        <div className="bg-[#12121A]/80 backdrop-blur-md p-6 rounded-2xl border-2 border-[#FFE800]/30 -rotate-2 shadow-xl shadow-[#FFE800]/5">
          <h2 className="font-display font-black text-xl text-[#FFE800] mb-3 uppercase tracking-wide">
             {t("seo_custom_title")}
          </h2>
          <p className="text-sm text-[#A0A0B0] leading-relaxed">
             {t("seo_custom_desc")}
          </p>
        </div>

      </div>

      {/* GŁÓWNY INTERFEJS GRY (Z Pana logiką) */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 w-full mt-10 lg:mt-0">
        <div className="relative z-10 w-full max-w-xl text-center">
          <div className="inline-flex items-center gap-3 mb-6 px-4 py-2 rounded-full border-2 border-white/10 bg-white/5">
            <Brain className="text-[#FFE800]" size={22} />
            <span className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#A0A0B0]">
              {t("play_from_anywhere")}
            </span>
          </div>

          <h1 className="font-display text-5xl sm:text-7xl font-black tracking-tighter leading-none mb-4">
            HEADS <span className="text-[#FFE800]">UP</span>
            <br />
            <span className="text-[#FF007F]">ONLINE</span>
          </h1>
          <p className="text-base sm:text-lg text-[#A0A0B0] mb-10 max-w-md mx-auto">{t("subtitle")}</p>

          {!mode && (
            <div className="flex flex-col sm:flex-row gap-5 justify-center pop-in">
              <button
                data-testid="create-room-button"
                onClick={() => setMode("create")}
                className="group flex items-center justify-center gap-3 min-h-[64px] px-8 rounded-2xl bg-[#FF007F] text-white font-display font-extrabold text-lg hard-shadow-white transition-transform duration-150 hover:-translate-y-1 relative z-20"
              >
                <Plus size={24} /> {t("create_room")}
              </button>
              <button
                data-testid="join-room-button"
                onClick={() => setMode("join")}
                className="group flex items-center justify-center gap-3 min-h-[64px] px-8 rounded-2xl bg-[#00FFFF] text-[#050510] font-display font-extrabold text-lg hard-shadow-white transition-transform duration-150 hover:-translate-y-1 relative z-20"
              >
                <LogIn size={24} /> {t("join_room")}
              </button>
            </div>
          )}

          {mode && (
            <form onSubmit={submit} className="pop-in max-w-sm mx-auto text-left space-y-4 relative z-20">
              {mode === "join" && (
                <div>
                  <label className="font-mono2 text-xs tracking-[0.2em] uppercase text-[#A0A0B0]">
                    {t("room_code")}
                  </label>
                  <input
                    data-testid="room-code-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    maxLength={4}
                    placeholder="ABCD"
                    className="mt-2 w-full text-center font-display text-3xl tracking-[0.4em] uppercase bg-[#12121A] border-2 border-white/10 rounded-2xl py-4 focus:outline-none focus:border-[#00FFFF]"
                  />
                </div>
              )}
              <div>
                <label className="font-mono2 text-xs tracking-[0.2em] uppercase text-[#A0A0B0]">
                  {t("your_name")}
                </label>
                <input
                  data-testid="player-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={16}
                  placeholder={t("enter_name")}
                  className="mt-2 w-full bg-[#12121A] border-2 border-white/10 rounded-2xl py-4 px-5 text-lg focus:outline-none focus:border-[#FFE800]"
                />
              </div>
              {error && <p className="text-[#FF007F] text-sm font-semibold">{error}</p>}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setMode(null)}
                  className="flex-1 min-h-[56px] rounded-2xl border-2 border-white/15 font-bold hover:bg-white/5 transition-colors"
                >
                  {t("back")}
                </button>
                <button
                  data-testid="submit-room-button"
                  type="submit"
                  disabled={loading || !name.trim() || (mode === "join" && code.length < 4)}
                  className="flex-[2] min-h-[56px] rounded-2xl bg-[#FFE800] text-[#050510] font-display font-extrabold text-lg disabled:opacity-40 transition-transform duration-150 hover:-translate-y-0.5"
                >
                  {loading ? "..." : mode === "create" ? t("lets_go") : t("join")}
                </button>
              </div>
            </form>
          )}

          <div className="mt-14 flex items-center justify-center gap-2 text-[#505060] text-sm relative z-20">
            <Users size={16} /> {t("players_note")}
          </div>

          <div className="mt-8 max-w-md mx-auto relative z-20">
            <GoogleAd format="auto" minHeight={120} label={t("advertisement")} />
          </div>
        </div>
      </div>

      {/* SEO MOBILNE (Chowa się na komputerach, ustawia na dole na telefonach) */}
      <div className="lg:hidden px-6 pb-12 space-y-6 max-w-md mx-auto relative z-10">
        <div className="h-px w-1/3 mx-auto bg-white/10 my-4"></div>
        <div className="text-center">
          <h2 className="font-display font-bold text-[#00FFFF] mb-2 text-lg">{t("seo_mobile_title1")}</h2>
          <p className="text-xs text-[#A0A0B0] leading-relaxed">{t("seo_mobile_desc1")}</p>
        </div>
        <div className="text-center">
          <h3 className="font-display font-bold text-[#FFE800] mb-2 text-lg">{t("seo_mobile_title2")}</h3>
          <p className="text-xs text-[#A0A0B0] leading-relaxed">{t("seo_mobile_desc2")}</p>
        </div>
      </div>

      <div className="w-full relative z-20">
        <LegalFooter screen="START" />
      </div>
    </div>
  );
}